<script setup lang="ts">
  import { computed, inject, type FunctionalComponent } from 'vue';
  import type { CompiledFieldNode, FieldValue } from '@record-fill';
  import { Tooltip } from '@/shared/ui';
  import type { CheckboxProperties, RadioGroupProperties } from '../../model/form.types';
  import { useElementContainerStyles } from '../../model/useElementContainerStyles';
  import { useLabelPosition } from '../../model/useLabelPosition';
  import FieldConfigurationBadge from '../elements/elements/FieldConfigurationBadge.vue';
  import { CHANGE_GROUP_CLASSES, fieldChange } from './change-marks';
  import { FILL_CONTEXT_KEY } from './fill-context';
  import { FILL_CONTROL_MAP } from './fill-control-map';

  const props = defineProps<{ node: CompiledFieldNode }>();
  const emit = defineEmits<{ (e: 'update:value', fieldId: string, value: FieldValue): void }>();

  const context = inject(FILL_CONTEXT_KEY, null);

  const properties = computed(() => props.node.element.properties);
  const { isLabelLeft, inputClasses, labelClasses, labelStyles } = useLabelPosition(properties);
  const { containerStyles } = useElementContainerStyles(properties, props.node.type);

  const control = computed(() => FILL_CONTROL_MAP[props.node.type]);
  const readOnly = computed(() => context?.readOnly.value === true);
  // Read-only is not disabled: a disabled control is drawn grey and faded, and the logs must stay
  // legible, so the control gets `readOnly` and keeps its normal look.
  const disabled = computed(() => !readOnly.value && (!props.node.state.enabled || !props.node.state.editable));
  const visibleLabel = computed(() => props.node.element.properties.label?.trim() ?? '');
  // The builder shows a helper text beside the label of the option groups, and the checkbox keeps
  // it in `placeholder`, which its editor labels "Texto auxiliar".
  const helperText = computed(() => {
    if (props.node.type === 'CHECKBOX') return (properties.value as CheckboxProperties).placeholder?.trim() ?? '';
    if (props.node.type === 'RADIO_GROUP') return (properties.value as RadioGroupProperties).helperText?.trim() ?? '';
    return '';
  });
  const PlainLabel: FunctionalComponent = (_props, { slots }) => slots.default?.();
  const tooltip = computed(() => properties.value.tooltip?.trim() ?? '');
  const labelWrapper = computed(() => (tooltip.value ? Tooltip : PlainLabel));
  const labelWrapperProps = computed(() => (tooltip.value ? { content: tooltip.value } : {}));
  // Many matrices carry the tooltip instead of a label (the neighbouring fixed text names the
  // field), so without a label the tooltip moves to the control itself.
  const tooltipOnControl = computed(() => tooltip.value.length > 0 && visibleLabel.value.length === 0);
  const change = computed(() => fieldChange(context, props.node));
  // A read-only control takes no focus, so the wrapper is the focus stop that shows the old value.
  // The wrapper is the Tooltip even with nothing to show: a correction mark appears on the first
  // key, and swapping the wrapper type there would remount the input and drop its focus.
  const controlTooltip = computed(() => change.value?.tooltip ?? (tooltipOnControl.value ? tooltip.value : ''));
  const focusableWrapper = computed(() => Boolean(change.value?.tooltip));
  // Read only, the controls take no focus (a disabled trigger, a group of inputs that cancel their
  // click), so a screen that points at a field moves the focus to this wrapper instead.
  const readOnlyFocus = computed(() =>
    readOnly.value ? { role: 'group', tabindex: -1, 'aria-label': visibleLabel.value || tooltip.value || props.node.label } : {},
  );
  const showsLeftLabelBlock = computed(() => isLabelLeft.value && helperText.value.length > 0);
  // A past snapshot is checked, not filled: a required field still empty at that response is not
  // an error to show.
  const issues = computed(() => {
    if (readOnly.value) return [];
    return [
      ...(context?.showCompiledIssues.value === false ? [] : props.node.issues),
      ...(context?.extraIssuesByField.value[props.node.id] ?? []),
    ];
  });
  const hasIssues = computed(() => issues.value.length > 0);
  const isHighlighted = computed(() => context?.highlightedFieldId.value === props.node.id);
  const isGroupControl = computed(() => props.node.type === 'CHECKBOX' || props.node.type === 'RADIO_GROUP');
  const changeClasses = computed(() => {
    if (!change.value) return '';
    const { tone } = change.value;
    return isGroupControl.value ? CHANGE_GROUP_CLASSES[tone] : `fill-field-change fill-field-change-${tone}`;
  });
  const showsAnchorBadge = computed(() => context?.showAnchorHighlights.value === true && props.node.tags.anchor);
  // The builder floats the badge at the field corner; a fill field inside a table cell has no
  // corner to spare (a checkbox row is 14px tall), so the badge sits in line with the control.
  const INLINE_BADGE_STYLE = { position: 'static' };

  function onUpdate(value: FieldValue): void {
    if (!readOnly.value) emit('update:value', props.node.id, value);
  }

  function notifyFocus(): void {
    context?.onFocusField(props.node.id);
  }
</script>

<template>
  <div
    v-if="node.state.visible"
    class="w-full min-w-0 max-w-full box-border [font-family:var(--font-base)]"
    :class="{
      'flex items-center': isLabelLeft,
    }"
    :style="containerStyles"
    v-bind="readOnlyFocus"
    :data-fill-field="node.id"
    :data-testid="`fill-field-${node.id}`"
    @focusin="notifyFocus"
    @click="notifyFocus"
  >
    <div
      v-if="showsLeftLabelBlock"
      class="mr-2 shrink-0"
    >
      <component
        :is="labelWrapper"
        v-if="visibleLabel"
        v-bind="labelWrapperProps"
      >
        <label
          :class="[labelClasses, 'block whitespace-nowrap']"
          :style="labelStyles"
        >
          {{ visibleLabel }}
        </label>
      </component>
      <p class="mt-1 text-[10px] text-gray-500">
        {{ helperText }}
      </p>
    </div>
    <component
      :is="labelWrapper"
      v-else-if="visibleLabel"
      v-bind="labelWrapperProps"
    >
      <label
        :class="[labelClasses, { 'shrink-0 whitespace-nowrap': isLabelLeft }]"
        :style="labelStyles"
      >
        {{ visibleLabel }}
        <span
          v-if="helperText"
          class="ml-1 text-[10px] font-normal text-gray-500"
        >({{ helperText }})</span>
      </label>
    </component>
    <div
      class="flex min-w-0 flex-col gap-0.5"
      :class="isLabelLeft ? 'w-0 flex-1' : 'w-full'"
    >
      <div class="flex min-w-0 items-center gap-1">
        <Tooltip
          :content="controlTooltip"
          :focusable="focusableWrapper"
          :class="['block w-full', changeClasses, {
            'min-w-0 flex-1': true,
            'fill-field-active': isHighlighted,
            'fill-field-invalid-group': hasIssues && isGroupControl,
            'fill-field-invalid': hasIssues && !isGroupControl,
          }]"
        >
          <component
            :is="control"
            :node="node"
            :disabled="disabled"
            :read-only="readOnly"
            :input-class="inputClasses"
            @update="onUpdate"
          />
        </Tooltip>
        <FieldConfigurationBadge
          v-if="showsAnchorBadge"
          class="shrink-0"
          configuration="anchor"
          :anchor-style="INLINE_BADGE_STYLE"
        />
      </div>
      <p
        v-for="(issue, index) in issues"
        :key="`${issue.code}-${index}`"
        class="text-[10px] text-red-600"
      >
        {{ issue.message }}
      </p>
    </div>
  </div>
</template>

<style scoped>
  /* The active field is marked on the control itself, never on a wrapper: an outline around the
     wrapper hugged the message with no margin. A soft outer glow in the branch color, which the
     fill table cell no longer clips; the error rules come after, so red wins on the same control. */
  .fill-field-active :deep(:is(input, select, textarea, [data-field-id] button)) {
    box-shadow:
      0 0 0 1px color-mix(in oklab, var(--theme-secondary) 18%, transparent),
      0 0 5px 2px color-mix(in oklab, var(--theme-secondary) 14%, transparent);
  }

  .fill-field-active :deep(:is(input[type='checkbox'], input[type='radio'])) {
    box-shadow:
      0 0 0 1px color-mix(in oklab, var(--theme-secondary) 22%, transparent),
      0 0 4px 2px color-mix(in oklab, var(--theme-secondary) 16%, transparent);
    border-radius: 2px;
  }

  .fill-field-change-added {
    --fill-change-fill: var(--color-change-added);
    --fill-change-border: var(--color-change-added-border);
  }

  .fill-field-change-fixed {
    --fill-change-fill: var(--color-change-fixed);
    --fill-change-border: var(--color-change-fixed-border);
  }

  .fill-field-change :deep(:is(input:not([type='checkbox'], [type='radio']), textarea, [data-field-id] button)) {
    background-color: var(--fill-change-fill);
    border-color: var(--fill-change-border);
  }

  .fill-field-invalid :deep(:is(input, select, textarea, [data-field-id] button)) {
    border-color: var(--color-red-500);
    box-shadow: none;
  }

  /* A native checkbox or radio has no border to paint, so each box gets a red outline instead of
     a ring around the whole option row, which read as a second control. */
  .fill-field-invalid-group :deep(:is(input[type='checkbox'], input[type='radio'])) {
    outline: 1px solid var(--color-red-500);
    outline-offset: 2px;
    border-radius: 2px;
  }
</style>
